import React, { Component } from "react";
import { useSearchParams, Link } from "react-router-dom";
import classes from "./VerifyEmail.module.css";
import axios from "../../../axios";

// Wrapper to use hooks with class component
function withRouter(Component) {
  return function WrappedComponent(props) {
    const [searchParams] = useSearchParams();
    return <Component {...props} searchParams={searchParams} />;
  };
}

class VerifyEmail extends Component {
  state = {
    loading: true,
    success: false,
    alreadyVerified: false,
    error: null,
    email: null,
    redirectUrl: null,
    token: null,
    sessionCode: null,
    redirectCountdown: 3
  };

  componentDidMount() {
    this.verifyEmail();
  }

  componentDidUpdate(prevProps, prevState) {
    // Start countdown when success state changes and redirectUrl is available
    if (this.state.success && this.state.redirectUrl && !prevState.success && !this.countdownInterval) {
      this.startRedirectCountdown();
    }
  }

  getRedirectUrlWithCode = () => {
    const { redirectUrl, sessionCode } = this.state;
    if (!redirectUrl || !sessionCode) return null;
    
    const separator = redirectUrl.includes('?') ? '&' : '?';
    return `${redirectUrl}${separator}code=${encodeURIComponent(sessionCode)}`;
  }

  componentWillUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  verifyEmail = async () => {
    const token = this.props.searchParams.get("token");

    if (!token) {
      this.setState({
        loading: false,
        error: "No verification token provided"
      });
      return;
    }

    try {
      const response = await axios.get(`/registration/verify-email?token=${token}`);

      if (response.data.token) {
        try {
          const jwtPayload = JSON.parse(atob(response.data.token.split('.')[1]));
          const tokenExpiration = jwtPayload.exp;

          localStorage.setItem("token", response.data.token);
          localStorage.setItem("username", response.data.email || jwtPayload.username || "");
          localStorage.setItem("tokenExpiration", tokenExpiration);

          // Broadcast login event so other account tabs update their state
          localStorage.setItem("account_login", Date.now().toString());
        } catch (e) {
        }
      }

      this.setState({
        loading: false,
        success: response.data.success,
        alreadyVerified: response.data.alreadyVerified,
        email: response.data.email,
        redirectUrl: response.data.redirectUrl,
        token: response.data.token,
        sessionCode: response.data.sessionCode || null
      });
    } catch (err) {
      let errorMessage = "Verification failed. Please try again.";
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      this.setState({
        loading: false,
        error: errorMessage
      });
    }
  };

  startRedirectCountdown = () => {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.countdownInterval = setInterval(() => {
      this.setState(prevState => {
        const newCountdown = prevState.redirectCountdown - 1;
        
        if (newCountdown <= 0) {
          clearInterval(this.countdownInterval);
          const finalUrl = this.getRedirectUrlWithCode();
          if (finalUrl) {
            window.location.href = finalUrl;
          }
        }
        
        return { redirectCountdown: newCountdown };
      });
    }, 1000);
  };

  render() {
    const { loading, success, alreadyVerified, error, email, redirectUrl, redirectCountdown } = this.state;

    // Loading state
    if (loading) {
      return (
        <div className={classes.pageContainer}>
          <div className={classes.messageContainer}>
            <div className={classes.loadingSpinner}></div>
            <h1 className={classes.title}>Verifying Your Email</h1>
            <p className={classes.message}>Please wait while we verify your email address...</p>
          </div>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className={classes.pageContainer}>
          <div className={classes.messageContainer}>
            <div className={classes.errorIcon}>✕</div>
            <h1 className={classes.title}>Verification Failed</h1>
            <p className={classes.message}>{error}</p>
            <Link to="/" className={classes.actionLink}>
              ← Back to Login
            </Link>
          </div>
        </div>
      );
    }

    // Success state (including already verified - treat as success)
    if (success) {
      return (
        <div className={classes.pageContainer}>
          <div className={classes.messageContainer}>
            <div className={classes.successIcon}>✓</div>
            <h1 className={classes.title}>Email Verified!</h1>
            <p className={classes.message}>
              {alreadyVerified ? (
                <>
                  Your email <strong>{email}</strong> was already verified.
                </>
              ) : (
                <>
                  Your email <strong>{email}</strong> has been successfully verified.
                </>
              )}
            </p>
            <p className={classes.submessage}>
              {alreadyVerified 
                ? "Redirecting you to the platform..."
                : "Your account has been created. Redirecting you to the platform..."
              }
            </p>
            
            {redirectUrl && (
              <>
                <p className={classes.redirectMessage}>
                  Redirecting back to platform in <strong>{redirectCountdown}</strong> seconds...
                </p>
                <button onClick={() => window.location.href = this.getRedirectUrlWithCode()} className={classes.actionButton}>
                  Continue Now
                </button>
              </>
            )}
          </div>
        </div>
      );
    }

    // Fallback
    return null;
  }
}

export default withRouter(VerifyEmail);
